import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { routes } from '@/config/routes';
import { useMeStore } from '@/store/meStore';
import { AppShell } from '@/components/layout/AppShell';
import { LandingPage } from '@/pages/Landing';
import { OnboardingPage } from '@/pages/Onboarding';
import { TodayPage } from '@/pages/Today';
import { BreakoutPage } from '@/pages/Breakout';
import { ImClosePage } from '@/pages/ImClose';
import { MomentumPage } from '@/pages/Momentum';
import { SettingsPage } from '@/pages/Settings';
import { RequireAuth } from '@/auth/RequireAuth';

const AuthPage = lazy(() => import('@/auth/AuthPage'));
const PassportCallbackPage = lazy(() => import('@/auth/PassportCallbackPage'));
const AuthHop = lazy(() => import('@/auth/AuthHop'));

function RequireOnboarded({ children }: { children: React.ReactNode }) {
  const complete = useMeStore((s) => s.profile?.onboardingComplete);
  if (!complete) return <Navigate to={routes.onboarding} replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-dvh items-center justify-center bg-hottr-ink text-white/55">
          <p className="font-display text-[11px] uppercase tracking-hottr-label">Loading…</p>
        </div>
      }
    >
      <Routes>
        <Route path={routes.auth} element={<AuthPage />} />
        <Route path={routes.authCallback} element={<PassportCallbackPage />} />
        <Route path={routes.authHop} element={<AuthHop />} />
        <Route path={routes.home} element={<LandingPage />} />
        <Route path={routes.onboarding} element={<OnboardingPage />} />
        <Route
          element={
            <RequireAuth>
              <RequireOnboarded>
                <AppShell />
              </RequireOnboarded>
            </RequireAuth>
          }
        >
          <Route path={routes.today} element={<TodayPage />} />
          <Route path={routes.breakout} element={<BreakoutPage />} />
          <Route path={routes.imClose} element={<ImClosePage />} />
          <Route path={routes.momentum} element={<MomentumPage />} />
          <Route path={routes.settings} element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to={routes.home} replace />} />
      </Routes>
    </Suspense>
  );
}
